import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OnboardingPage from './pages/OnboardingPage';
import ProfilePage from './pages/ProfilePage';
import SurveysPage from './pages/SurveysPage';
import EarningHistoryPage from './pages/EarningHistoryPage';
import RedemptionPage from './pages/RedemptionPage';
import SurveyWaitingPage from './pages/SurveyWaitingPage';
import GamesPage from './pages/GamesPage';
import SwipePage from './pages/SwipePage';
import TrustCenterPage from './pages/TrustCenterPage';
import ReferralPage from './pages/ReferralPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import SurveyCompletePage from './pages/survey/SurveyCompletePage';
import SurveyScreenoutPage from './pages/survey/SurveyScreenoutPage';
import SurveyQuotafullPage from './pages/survey/SurveyQuotafullPage';
import SurveyTerminatePage from './pages/survey/SurveyTerminatePage';
import TestSurveyPage from './pages/survey/TestSurveyPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import SmsTermsPage from './pages/SmsTermsPage';
import UnsubscribePage from './pages/UnsubscribePage';
import AccountIneligiblePage from './pages/AccountIneligiblePage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMembers, { AdminMemberDetail } from './pages/admin/AdminMembers';
import AdminSurveys from './pages/admin/AdminSurveys';
import AdminGames from './pages/admin/AdminGames';
import AdminBuyers from './pages/admin/AdminBuyers';
import AdminBuyerDetail from './pages/admin/AdminBuyerDetail';
import AdminProjects from './pages/admin/AdminProjects';
import AdminProjectForm from './pages/admin/AdminProjectForm';
import AdminConfig from './pages/admin/AdminConfig';
import AdminBreakage from './pages/admin/AdminBreakage';
import AdminUA from './pages/admin/AdminUA';
import AdminSurveyBuilder from './pages/admin/AdminSurveyBuilder';
import AdminEmailAnalytics from './pages/admin/AdminEmailAnalytics';
import ProfileIntelligencePage from './pages/admin/ProfileIntelligencePage';
import FraudPanel from './pages/admin/FraudPanel';
import BuyerQualityPage from './pages/admin/BuyerQualityPage';
import AdminSimulator from './pages/admin/AdminSimulator';
import AdminCare from './pages/admin/AdminCare';
import AdminRedemptionQueue from './pages/admin/AdminRedemptionQueue';
import AdminRedemptionDetail from './pages/admin/AdminRedemptionDetail';
import AdminTicketDetail from './pages/admin/AdminTicketDetail';
import { useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { useMe } from './hooks/useProfile';
import { isAccountIneligible } from './lib/api';
import { captureClick } from './lib/ua';
import { trackPageView, trackUTMParams } from './lib/analytics';

/**
 * Gate component that redirects to onboarding if the member has not completed it.
 * Shows the children only when onboarding is complete.
 */
export function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading, error } = useMe();

  // Ejected member (suspended/blocked/deleted): the member-load 403s with
  // ACCOUNT_INELIGIBLE. Show the member-care dead-end INSTEAD of the empty
  // protected shell. Keyed strictly on the code — inactive/opted_out load
  // normally (no 403) and never reach here.
  if (isAccountIneligible(error)) {
    return <AccountIneligiblePage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (me && !me.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading, error } = useMe();

  // Same ejected front-door as RequireOnboarding — an ejected admin sees the
  // care screen, not a bounce to /dashboard.
  if (isAccountIneligible(error)) {
    return <AccountIneligiblePage />;
  }

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950"><div className="text-slate-400">Loading...</div></div>;
  }

  if (!me?.member || me.member.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { session, loading } = useAuth();
  const location = useLocation();
  // Kick off GET /api/me as soon as the session is available so all dependent
  // queries (profile, surveys) are unblocked after it resolves.
  useMe(!!session);

  // Capture UA click on app load
  useEffect(() => { void captureClick(); }, []);

  // GA4: persist UTM params on the session (one-shot, mount only)
  useEffect(() => { trackUTMParams(); }, []);

  // GA4: SPA page view on every route change. Initial pageview is suppressed
  // in index.html so this fires once on first mount via the location dep too.
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={session ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route path="/auth/google/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* Public survey outcome pages — no auth required (provider redirects here) */}
      <Route path="/survey/complete" element={<SurveyCompletePage />} />
      <Route path="/survey/screenout" element={<SurveyScreenoutPage />} />
      <Route path="/survey/quotafull" element={<SurveyQuotafullPage />} />
      <Route path="/survey/security-terminate" element={<SurveyTerminatePage />} />
      <Route path="/survey/test/:surveyId" element={session ? <TestSurveyPage /> : <Navigate to="/login" replace />} />
      <Route
        path="/dashboard"
        element={session ? <RequireOnboarding><DashboardPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/onboarding"
        element={session ? <OnboardingPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/profile"
        element={session ? <RequireOnboarding><ProfilePage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/surveys"
        element={session ? <RequireOnboarding><SurveysPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/earning-history"
        element={session ? <RequireOnboarding><EarningHistoryPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      {/* Legacy path — /survey-history was replaced by the ledger-backed earnings
          history. Redirect so old links/bookmarks/emails don't break. */}
      <Route path="/survey-history" element={<Navigate to="/earning-history" replace />} />
      <Route
        path="/games"
        element={session ? <RequireOnboarding><GamesPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/swipe"
        element={session ? <RequireOnboarding><SwipePage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/trust-center"
        element={session ? <RequireOnboarding><TrustCenterPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/referral"
        element={session ? <RequireOnboarding><ReferralPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/redeem"
        element={session ? <RequireOnboarding><RedemptionPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route
        path="/survey/:assignmentId/waiting"
        element={session ? <RequireOnboarding><SurveyWaitingPage /></RequireOnboarding> : <Navigate to="/login" replace />}
      />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/sms-terms" element={<SmsTermsPage />} />
      <Route path="/admin" element={session ? <RequireAdmin><AdminLayout /></RequireAdmin> : <Navigate to="/login" replace />}>
        <Route index element={<AdminDashboard />} />
        <Route path="members" element={<AdminMembers />} />
        <Route path="members/:id" element={<AdminMemberDetail />} />
        <Route path="surveys" element={<AdminSurveys />} />
        <Route path="simulator" element={<AdminSimulator />} />
        <Route path="surveys/new" element={<AdminSurveyBuilder />} />
        <Route path="surveys/:id/edit" element={<AdminSurveyBuilder />} />
        <Route path="games" element={<AdminGames />} />
        <Route path="buyers" element={<AdminBuyers />} />
        <Route path="buyers/:id" element={<AdminBuyerDetail />} />
        <Route path="projects" element={<AdminProjects />} />
        <Route path="manual-projects/new" element={<AdminProjectForm />} />
        <Route path="manual-projects/:id" element={<AdminProjectForm />} />
        <Route path="ua" element={<AdminUA />} />
        <Route path="config" element={<AdminConfig />} />
        <Route path="breakage" element={<AdminBreakage />} />
        <Route path="email" element={<AdminEmailAnalytics />} />
        <Route path="profile-intelligence" element={<ProfileIntelligencePage />} />
        <Route path="fraud" element={<FraudPanel />} />
        <Route path="buyer-quality" element={<BuyerQualityPage />} />
        {/* Merged Member Care surface — URL-driven Chat | Tickets tabs. */}
        <Route path="care" element={<Navigate to="/admin/care/chat" replace />} />
        <Route path="care/:tab" element={<AdminCare />} />
        {/* Legacy routes redirect into the merged surface (preserve bookmarks/inbound links). */}
        <Route path="support-chats" element={<Navigate to="/admin/care/chat" replace />} />
        <Route path="redemptions" element={<AdminRedemptionQueue />} />
        <Route path="redemptions/:id" element={<AdminRedemptionDetail />} />
        <Route path="tickets" element={<Navigate to="/admin/care/tickets" replace />} />
        {/* Ticket detail (with write actions) stays its own route. */}
        <Route path="tickets/:id" element={<AdminTicketDetail />} />
      </Route>
      <Route path="*" element={<Navigate to={session ? '/dashboard' : '/login'} replace />} />
    </Routes>
  );
}
